import { Link } from '@tanstack/react-router';
import { LogOut, Moon, Sun, UserRound } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { attemptLeave } from '../lib/unsaved.tsx';
import { useSession } from '../lib/session.tsx';
import { useTheme } from '../lib/theme.ts';
import { cn } from '../lib/utils.ts';
import { Popover } from './ui/popover.tsx';

/**
 * Zeile „Dunkles Design“ (Profil-Menü und Menü-Schublade am Handy): fester Name mit `aria-pressed`, Schalter-Optik.
 * Ohne eigene Wahl hell; die Wahl wird je Person im Profil gemerkt (EXPERIENCE §1, §4.1).
 */
export function ThemeRow() {
  const { theme, toggle } = useTheme();
  const dark = theme === 'dark';
  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={dark}
      className="flex h-11 w-full cursor-pointer items-center gap-3 rounded-field px-3 text-row text-ink-2 hover:bg-tile"
    >
      {dark ? <Sun size={18} strokeWidth={1.5} aria-hidden /> : <Moon size={18} strokeWidth={1.5} aria-hidden />}
      Dunkles Design
      <span
        aria-hidden
        className={cn(
          'relative ml-auto inline-flex h-6 w-11 items-center rounded-full border-[1.5px]',
          dark ? 'border-brand bg-brand' : 'border-checkbox-line bg-fill',
        )}
      >
        <span className={cn('block size-[17px] rounded-full', dark ? 'translate-x-[21px] bg-on-brand' : 'translate-x-[2px] bg-checkbox-line')} />
      </span>
    </button>
  );
}

/**
 * Profil-Menü (Change 11.10.2026 Design S3, EXPERIENCE §2.4 und §11 Frage 49): Klick auf den Profil-Kreis in der
 * Seitenleiste öffnet ein kleines Menü mit Name, „Profil öffnen“, „Dunkles Design“ und „Abmelden“. Der Hell/Dunkel-Schalter
 * sitzt seit S3 hier statt in der Kopfzeile. Öffnet nach oben, weil der Kreis unten in der Leiste steht.
 */
export function ProfileMenu({ children, onNavigate }: { children: ReactNode; onNavigate?: () => void }) {
  const { me, auth } = useSession();
  const [open, setOpen] = useState(false);
  const close = () => {
    setOpen(false);
    onNavigate?.();
  };
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      label="Persönliches Menü"
      className="top-auto bottom-[calc(100%+8px)] w-[min(260px,calc(100vw-32px))] p-2"
      trigger={(props) => (
        <button
          type="button"
          {...props}
          // Name ohne „Profil“: die Gruppe „Profil“ in der Leiste soll eindeutig auffindbar bleiben.
          aria-label={`Persönliches Menü: ${me.displayName}`}
          className="grid size-10 flex-none cursor-pointer place-items-center rounded-full pointer-coarse:size-11"
        >
          {children}
        </button>
      )}
    >
      <div className="px-3 pt-1 pb-2">
        <b className="block truncate text-row font-medium text-ink">{me.displayName}</b>
        <span className="block truncate text-caption text-ink-3">{me.company}</span>
      </div>
      <div className="flex flex-col border-t border-line pt-1 pointer-coarse:gap-1">
        <Link
          to="/profil"
          onClick={close}
          className="flex h-11 items-center gap-3 rounded-field px-3 text-row text-ink-2 hover:bg-tile"
        >
          <UserRound size={18} strokeWidth={1.5} aria-hidden />
          Profil öffnen
        </Link>
        <ThemeRow />
        <button
          type="button"
          onClick={() => {
            close();
            // Ungespeicherte Änderungen im offenen Datensatz: erst die Leiste dort (EXPERIENCE §4.3).
            attemptLeave(() => void auth.signOut());
          }}
          className="flex h-11 w-full cursor-pointer items-center gap-3 rounded-field px-3 text-row text-ink-2 hover:bg-tile"
        >
          <LogOut size={18} strokeWidth={1.5} aria-hidden />
          Abmelden
        </button>
      </div>
    </Popover>
  );
}
